import React, { useRef, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Spinner, EmptyState, FileUploadDropzone, StatusBadge, PhoneInput } from '@/components/common';
import { useAuthStore } from '@/store/authStore';
import { useCompanies, useCreateCompany, useUpdateCompany } from '@/hooks/useCompany';
import { imageApi } from '@/api/imageApi';
import { Upload, Plus, Edit3, ArrowLeft, Loader2, UploadCloud, X, Eye } from 'lucide-react';
import { Tooltip, IconButton } from '@mui/material';
import { useUIStore } from '@/store/uiStore';
import AddressSelector from '@/components/common/AddressSelector';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Company } from '@/types';
import { useJobCategories } from '@/hooks/useJob';

interface UploadedImg {
  id?: string;
  url: string;
  file?: File;
}

const DEFAULT_INDUSTRY_OPTIONS = [
  { value: 'Nhà hàng', label: 'Nhà hàng' },
  { value: 'Cà phê / Trà sữa', label: 'Cà phê / Trà sữa' },
  { value: 'Quán ăn / Quán nhậu', label: 'Quán ăn / Quán nhậu' },
  { value: 'Bar / Pub / Beer Garden', label: 'Bar / Pub / Beer Garden' },
  { value: 'Bếp trung tâm (Central Kitchen)', label: 'Bếp trung tâm (Central Kitchen)' },
  { value: 'Thức ăn nhanh (Fast Food)', label: 'Thức ăn nhanh (Fast Food)' },
  { value: 'Bánh ngọt / Tiệm bánh', label: 'Bánh ngọt / Tiệm bánh' },
  { value: 'Khách sạn / Resort F&B', label: 'Khách sạn / Resort F&B' },
  { value: 'Chế biến & Phân phối thực phẩm', label: 'Chế biến & Phân phối thực phẩm' },
  { value: 'Take-away / Cloud Kitchen', label: 'Take-away / Cloud Kitchen' },
];

const SIZE_OPTIONS = [

  { value: 'Dưới 50 nhân viên', label: 'Dưới 50 nhân viên' },
  { value: '50 - 200 nhân viên', label: '50 - 200 nhân viên' },
  { value: '200 - 500 nhân viên', label: '200 - 500 nhân viên' },
  { value: 'Trên 500 nhân viên', label: 'Trên 500 nhân viên' },
];

const companySchema = z.object({
  name: z.string().min(2, 'Vui lòng nhập tên công ty'),
  taxCode: z.string().min(2, 'Vui lòng nhập mã số thuế'),
  industry: z.string().optional(),
  size: z.string().optional(),
  foundedYear: z.coerce.number().min(1900, 'Năm thành lập không hợp lệ').max(new Date().getFullYear()),
  description: z.string().min(10, 'Nhập mô tả tối thiểu 10 ký tự'),
  website: z.string().optional().transform(v => {
    if (!v) return "";
    if (!/^https?:\/\//i.test(v)) return `https://${v}`;
    return v;
  }).refine(val => {
    if (!val) return true;
    return /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/.test(val);
  }, { message: 'Website không hợp lệ. Vui lòng nhập đúng định dạng (VD: https://domain.com)' }),
  email: z.string().email('Email không hợp lệ'),
  phone: z.string().min(1, 'Vui lòng nhập số điện thoại'),
  address: z.string().min(5, 'Địa chỉ không hợp lệ'),
  numberOfBranches: z.coerce.number().min(0, 'Vui lòng nhập số chi nhánh'),
  countryId: z.coerce.number().default(1),
  provinceId: z.preprocess((val) => (val && !isNaN(Number(val)) && Number(val) > 0 ? Number(val) : undefined), z.number({ required_error: 'Vui lòng chọn tỉnh/thành phố', invalid_type_error: 'Vui lòng chọn tỉnh/thành phố' }).min(1, 'Vui lòng chọn tỉnh/thành phố')),
  districtId: z.preprocess((val) => (val && !isNaN(Number(val)) && Number(val) > 0 ? Number(val) : undefined), z.number({ required_error: 'Vui lòng chọn quận/huyện', invalid_type_error: 'Vui lòng chọn quận/huyện' }).min(1, 'Vui lòng chọn quận/huyện')),
  wardId: z.preprocess((val) => (val && !isNaN(Number(val)) && Number(val) > 0 ? Number(val) : undefined), z.number({ required_error: 'Vui lòng chọn phường/xã', invalid_type_error: 'Vui lòng chọn phường/xã' }).min(1, 'Vui lòng chọn phường/xã')),
  operatingStatus: z.string().default('active'),
  openingHours: z.string().default('08:00 - 17:00'),
});

type CompanyFormData = z.infer<typeof companySchema>;

export default function CompanyProfileSettings() {
  const { user } = useAuthStore();
  const { data: companiesResponse, isLoading: isLoadingCompanies } = useCompanies({ createdBy: user?.id, page: 1, limit: 50 });
  const companies = companiesResponse?.data || [];
  const { data: categories } = useJobCategories();

  const industryOptions = React.useMemo(() => {
    const list = [...DEFAULT_INDUSTRY_OPTIONS];
    if (categories && categories.length > 0) {
      categories.forEach(c => {
        if (c.name && !list.some(item => item.value.toLowerCase() === c.name.toLowerCase())) {
          list.push({ value: c.name, label: c.name });
        }
      });
    }
    return list;
  }, [categories]);

  const { mutate: createCompany, isPending: isCreating } = useCreateCompany();
  const { mutate: updateCompany, isPending: isUpdating } = useUpdateCompany();
  const { addToast } = useUIStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const modeParam = searchParams.get('mode');
  const companyIdParam = searchParams.get('companyId');

  const viewMode: 'list' | 'create' | 'edit' = modeParam === 'create' || modeParam === 'edit' ? modeParam : 'list';
  const editingId = companyIdParam || null;

  const setViewMode = (mode: 'list' | 'create' | 'edit', companyId?: string | null) => {
    if (mode === 'list') {
      setSearchParams({});
    } else if (mode === 'create') {
      setSearchParams({ mode: 'create' });
    } else if (mode === 'edit' && companyId) {
      setSearchParams({ mode: 'edit', companyId });
    }
  };

  const [logoImage, setLogoImage] = useState<UploadedImg | null>(null);
  const [licenseImage, setLicenseImage] = useState<UploadedImg | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const licenseInputRef = useRef<HTMLInputElement>(null);

  const { register, handleSubmit, formState: { errors }, watch, setValue, reset, control } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      industry: '',
      operatingStatus: 'active',
      openingHours: '08:00 - 17:00',
      size: '1-50',
      countryId: 1
    }
  });

  // Tự động load dữ liệu khi URL chứa ?mode=edit&companyId=...
  useEffect(() => {
    if (modeParam === 'edit' && companyIdParam && companies.length > 0) {
      const found = companies.find(c => c.id === companyIdParam);
      if (found) {
        reset({
          name: found.name,
          taxCode: found.taxCode,
          industry: found.industry || found.businessSectorName || '',
          size: found.size || '',
          foundedYear: found.foundedYear,
          description: found.description,
          website: found.website || '',
          email: found.email || '',
          phone: found.phone || '',
          address: found.address,
          numberOfBranches: found.numberOfBranches || 1,
          countryId: found.countryId || 1,
          provinceId: found.provinceId,
          districtId: found.districtId,
          wardId: found.wardId,
          operatingStatus: found.operatingStatus || 'active',
          openingHours: found.openingHours || '08:00 - 17:00',
        });
        if (found.logoUrl) setLogoImage({ url: found.logoUrl });
        if (found.businessLicenseImage) setLicenseImage({ id: found.businessLicenseImage.id, url: found.businessLicenseImage.publicUrl });
      }
    }
  }, [modeParam, companyIdParam, companies]);

  const handleEdit = (c: Company) => {
    reset({
      name: c.name,
      taxCode: c.taxCode,
      industry: c.industry || c.businessSectorName || '',
      size: c.size || '',
      foundedYear: c.foundedYear,
      description: c.description,
      website: c.website || '',
      email: c.email || '',
      phone: c.phone || '',
      address: c.address,
      numberOfBranches: c.numberOfBranches || 1,
      countryId: c.countryId || 1,
      provinceId: c.provinceId,
      districtId: c.districtId,
      wardId: c.wardId,
      operatingStatus: c.operatingStatus || 'active',
      openingHours: c.openingHours || '08:00 - 17:00',
    });

    if (c.logoUrl) {
      setLogoImage({ url: c.logoUrl });
    } else {
      setLogoImage(null);
    }

    if (c.businessLicenseImage) {
      setLicenseImage({ id: c.businessLicenseImage.id, url: c.businessLicenseImage.publicUrl });
    } else {
      setLicenseImage(null);
    }

    setViewMode('edit', c.id);
  };

  const handleCreateNew = () => {
    reset({
      name: '',
      taxCode: '',
      industry: '',
      size: '1-50',
      foundedYear: new Date().getFullYear(),
      description: '',
      website: '',
      email: '',
      phone: '',
      address: '',
      numberOfBranches: 1,
      countryId: 1,
      operatingStatus: 'active',
      openingHours: '08:00 - 17:00',
    });
    setLogoImage(null);
    setLicenseImage(null);
    setViewMode('create');
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      addToast('error', `File ${file.name} không hợp lệ.`);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      addToast('error', `File ${file.name} vượt quá dung lượng 5MB.`);
      return;
    }

    setLogoImage({
      url: URL.createObjectURL(file),
      file,
    });
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleLicenseUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      addToast('error', `File ${file.name} không hợp lệ. Vui lòng chọn ảnh hoặc PDF.`);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      addToast('error', `File ${file.name} vượt quá dung lượng 5MB.`);
      return;
    }

    setLicenseImage({
      url: file.type === 'application/pdf' ? 'https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg' : URL.createObjectURL(file),
      file,
    });
    if (licenseInputRef.current) licenseInputRef.current.value = '';
  };

  const onSubmit = async (data: CompanyFormData) => {
    if (viewMode === 'create' && (!logoImage || !licenseImage)) {
      addToast('error', 'Vui lòng tải lên cả Ảnh công ty (Logo) và Giấy phép ĐKKD');
      return;
    }

    try {
      setIsUploading(true);
      let finalLogoUrl = logoImage?.url;
      let finalLicenseId = licenseImage?.id;

      if (logoImage?.file) {
        const res = await imageApi.upload(logoImage.file);
        if (res && res.data) {
          finalLogoUrl = res.data.publicUrl;
        }
      }

      if (licenseImage?.file) {
        const res = await imageApi.upload(licenseImage.file);
        if (res && res.data) {
          finalLicenseId = res.data.id;
        }
      }

      const payload = {
        ...data,
        logoUrl: finalLogoUrl,
        businessLicenseImageId: finalLicenseId
      };

      if (viewMode === 'create') {
        createCompany(payload, {
          onSuccess: () => {
            setViewMode('list');
          }
        });
      } else if (viewMode === 'edit' && editingId) {
        updateCompany({
          id: editingId,
          data: payload
        }, {
          onSuccess: () => {
            setViewMode('list');
          }
        });
      }
    } catch (err: any) {
      console.error(err);
      addToast('error', 'Lỗi khi tải ảnh lên. Vui lòng thử lại.');
    } finally {
      setIsUploading(false);
    }
  };

  if (isLoadingCompanies) return <div className="py-10 text-center"><Spinner /></div>;

  if (viewMode === 'list') {
    return (
      <div className="space-y-6 animate-fade-in text-left">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-5">
          <div>
            <h1 className="font-display font-bold text-2xl text-gray-900 leading-tight">Thông tin công ty</h1>
          </div>
          <Button onClick={handleCreateNew} className="bg-[#f97316] hover:bg-[#e04700] text-white font-bold h-10 px-5 rounded-xl flex items-center gap-2 transition-all">
            <Plus size={16} /> Tạo thêm công ty
          </Button>
        </div>

        {companies.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
            <h3 className="font-bold text-lg text-gray-800 mb-2">Chưa có công ty nào</h3>
            <p className="text-xs text-gray-400 mb-6">Bạn chưa tạo hồ sơ doanh nghiệp nào trên hệ thống.</p>
            <Button onClick={handleCreateNew} className="bg-[#f97316] hover:bg-[#e04700] text-white rounded-xl px-6 font-bold shadow-md h-10">
              Tạo công ty đầu tiên
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-[#f8fafc] border-b border-[#f1f3f5] text-xs font-bold text-[#64748b] uppercase tracking-wider">
                  <tr>
                    <th className="py-4 px-6">Công ty</th>
                    <th className="py-4 px-6">Mã số thuế</th>
                    <th className="py-4 px-6">Ngành nghề & Quy mô</th>
                    <th className="py-4 px-6">Trạng thái</th>
                    <th className="py-4 px-6 text-right">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f3f5]">
                  {companies.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-2xl border border-gray-200 overflow-hidden shrink-0 flex items-center justify-center bg-gray-50 shadow-xs">
                            <img
                              src={c.logoUrl || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=300"}
                              alt="Logo"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <span className="font-bold text-gray-900 block leading-tight">{c.name}</span>
                            {c.email && <span className="text-xs text-gray-400">{c.email}</span>}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 font-semibold text-gray-700">
                        {c.taxCode}
                      </td>
                      <td className="py-4 px-6 text-gray-600">
                        <span className="font-semibold text-gray-900 block">{c.industry}</span>
                        <span className="text-xs text-gray-400">
                          {c.size ? (c.size.includes('nhân viên') ? c.size : `${c.size} nhân viên`) : ''}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <StatusBadge
                          status={c.approvalStatus || 'pending'}
                        />
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Tooltip title="Xem chi tiết" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(c)}
                              sx={{
                                border: '1px solid #f1f3f5',
                                borderRadius: '12px',
                                '&:hover': { backgroundColor: '#fff7ed', color: '#f97316' }
                              }}
                            >
                              <Eye size={15} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Cập nhật" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(c)}
                              sx={{
                                border: '1px solid #f1f3f5',
                                borderRadius: '12px',
                                '&:hover': { backgroundColor: '#f1f3f5', color: '#1e293b' }
                              }}
                            >
                              <Edit3 size={15} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in text-left">
      <div className="flex items-center gap-3 border-b pb-5">
        <Button variant="ghost" onClick={() => setViewMode('list')} className="p-2 border border-gray-200 rounded-xl hover:bg-gray-50 h-10 w-10 shrink-0">
          <ArrowLeft size={16} />
        </Button>
        <div>
          <h1 className="font-display font-bold text-2xl text-gray-900 leading-tight">
            {viewMode === 'edit' ? 'Cập nhật công ty' : 'Tạo công ty mới'}
          </h1>
          <p className="text-xs text-gray-500 font-medium mt-1">
            {viewMode === 'edit' ? 'Cập nhật thông tin doanh nghiệp của bạn.' : 'Cung cấp thông tin doanh nghiệp và tải lên GPĐKKD để được xác thực.'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm space-y-6">
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Tên công ty *</label>
            <input {...register('name')} className="w-full px-4 h-11 rounded-xl border border-gray-200 outline-none focus:border-orange-500 text-sm" placeholder="Ví dụ: Công ty TNHH ABC" />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Mã số thuế *</label>
            <input {...register('taxCode')} className="w-full px-4 h-11 rounded-xl border border-gray-200 outline-none focus:border-orange-500 text-sm" placeholder="Ví dụ: 0101234567" />
            {errors.taxCode && <p className="text-red-500 text-xs mt-1">{errors.taxCode.message}</p>}
          </div>

          <div>
            <Controller
              name="industry"
              control={control}
              render={({ field }) => (
                <Select
                  label="Lĩnh vực kinh doanh"
                  placeholder="Chọn gợi ý hoặc tự nhập lĩnh vực..."
                  options={industryOptions}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target ? e.target.value : e)}
                  error={errors.industry?.message}
                  allowCustom
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="size"
              control={control}
              render={({ field }) => (
                <Select
                  label="Quy mô"
                  placeholder="Chọn gợi ý hoặc tự nhập quy mô..."
                  options={SIZE_OPTIONS}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target ? e.target.value : e)}
                  error={errors.size?.message}
                  allowCustom
                />
              )}
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Năm thành lập *</label>
            <input type="number" {...register('foundedYear')} className="w-full px-4 h-11 rounded-xl border border-gray-200 outline-none focus:border-orange-500 text-sm" placeholder="2020" />
            {errors.foundedYear && <p className="text-red-500 text-xs mt-1">{errors.foundedYear.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Số chi nhánh *</label>
            <input type="number" {...register('numberOfBranches')} className="w-full px-4 h-11 rounded-xl border border-gray-200 outline-none focus:border-orange-500 text-sm" placeholder="1" />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Email liên hệ *</label>
            <input {...register('email')} className="w-full px-4 h-11 rounded-xl border border-gray-200 outline-none focus:border-orange-500 text-sm" placeholder="contact@company.com" />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
          </div>

          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <PhoneInput
                label="Số điện thoại"
                required
                value={field.value || ''}
                onChange={(val) => field.onChange(val)}
                error={errors.phone?.message}
              />
            )}
          />

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Website</label>
            <input {...register('website')} className="w-full px-4 h-11 rounded-xl border border-gray-200 outline-none focus:border-orange-500 text-sm" placeholder="https://..." />
            {errors.website && <p className="text-red-500 text-xs mt-1">{errors.website.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Giờ mở cửa</label>
            <input {...register('openingHours')} className="w-full px-4 h-11 rounded-xl border border-gray-200 outline-none focus:border-orange-500 text-sm" placeholder="10:00 - 22:00" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1">Địa chỉ chi tiết *</label>
          <input {...register('address')} className="w-full px-4 h-11 rounded-xl border border-gray-200 outline-none focus:border-orange-500 text-sm" placeholder="123 Đường ABC, Phường XYZ" />
          {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address.message}</p>}
        </div>

        <div className="pt-2">
          <AddressSelector
            countryId={watch('countryId')}
            provinceId={watch('provinceId')}
            districtId={watch('districtId')}
            wardId={watch('wardId')}
            onCountryChange={(id) => {
              setValue('countryId', id, { shouldValidate: true, shouldDirty: true });
              setValue('provinceId', undefined as any, { shouldValidate: true });
              setValue('districtId', undefined as any, { shouldValidate: true });
              setValue('wardId', undefined as any, { shouldValidate: true });
            }}
            onProvinceChange={(id) => {
              setValue('provinceId', id, { shouldValidate: true, shouldDirty: true });
              setValue('districtId', undefined as any, { shouldValidate: true });
              setValue('wardId', undefined as any, { shouldValidate: true });
            }}
            onDistrictChange={(id) => {
              setValue('districtId', id, { shouldValidate: true, shouldDirty: true });
              setValue('wardId', undefined as any, { shouldValidate: true });
            }}
            onWardChange={(id) => {
              setValue('wardId', id, { shouldValidate: true, shouldDirty: true });
            }}
            required
            errors={{
              countryId: errors.countryId?.message,
              provinceId: errors.provinceId?.message,
              districtId: errors.districtId?.message,
              wardId: errors.wardId?.message,
            }}
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1">Mô tả công ty *</label>
          <textarea {...register('description')} rows={4} className="w-full p-4 rounded-xl border border-gray-200 outline-none focus:border-orange-500 text-sm resize-none" placeholder="Giới thiệu về công ty..." />
          {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description.message}</p>}
        </div>

        {/* File Upload Section */}
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          {/* Logo Upload */}
          <div className="space-y-4">
            <label className="block text-xs font-bold text-gray-700">Ảnh công ty (Logo) *</label>
            {!logoImage ? (
              <div className="space-y-3">
                <FileUploadDropzone
                  onFileSelect={(files) => {
                    const file = files[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        addToast('error', `File ${file.name} vượt quá dung lượng 5MB.`);
                        return;
                      }
                      setLogoImage({ url: URL.createObjectURL(file), file });
                    }
                  }}
                  isUploading={isUploading}
                  accept="image/*,.pdf"
                  title="Tải lên Logo"
                  subtitle="Hỗ trợ JPG, PNG, PDF. Tối đa 5MB"
                />
                <div className="flex items-center gap-2">
                  <div className="h-px bg-gray-200 flex-1"></div>
                  <span className="text-[10px] font-bold text-gray-400 uppercase">Hoặc</span>
                  <div className="h-px bg-gray-200 flex-1"></div>
                </div>
                <input
                  type="text"
                  placeholder="Dán đường dẫn (URL) ảnh từ server khác..."
                  className="w-full px-4 h-10 rounded-xl border border-gray-200 outline-none focus:border-orange-500 text-sm"
                  onBlur={(e) => {
                    const val = e.target.value.trim();
                    if (val) {
                      setLogoImage({ url: val });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = e.currentTarget.value.trim();
                      if (val) {
                        setLogoImage({ url: val });
                      }
                    }
                  }}
                />
              </div>
            ) : (
              <div className="relative aspect-video rounded-xl overflow-hidden border-2 border-gray-200 group h-[200px]">
                <img src={logoImage.url} alt="Logo" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => setLogoImage(null)}
                    className="w-10 h-10 rounded-full bg-white/20 hover:bg-red-500 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Business License Upload */}
          <div className="space-y-4">
            <label className="block text-xs font-bold text-gray-700">Giấy phép ĐKKD *</label>
            {!licenseImage ? (
              <FileUploadDropzone
                onFileSelect={(files) => {
                  const file = files[0];
                  if (file) {
                    if (file.size > 5 * 1024 * 1024) {
                      addToast('error', `File ${file.name} vượt quá dung lượng 5MB.`);
                      return;
                    }
                    setLicenseImage({
                      url: file.type === 'application/pdf' ? 'https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg' : URL.createObjectURL(file),
                      file
                    });
                  }
                }}
                isUploading={isUploading}
                accept="image/*,.pdf"
                title="Tải lên GPĐKKD"
                subtitle="Hỗ trợ JPG, PNG, PDF. Tối đa 5MB"
              />
            ) : (
              <div className="relative aspect-video rounded-xl overflow-hidden border-2 border-gray-200 group h-[200px] flex items-center justify-center bg-gray-50">
                <img src={licenseImage.url} alt="GPĐKKD" className={cn("w-full h-full", licenseImage.url.includes('PDF_file_icon') ? "object-contain p-4" : "object-cover")} />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => setLicenseImage(null)}
                    className="w-10 h-10 rounded-full bg-white/20 hover:bg-red-500 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end border-t border-gray-100 pt-5">
          <Button type="submit" disabled={isCreating || isUpdating || isUploading} className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl h-11 px-8 font-bold">
            {(isCreating || isUpdating || isUploading) ? <Spinner size="sm" /> : (viewMode === 'edit' ? 'Cập nhật hồ sơ' : 'Tạo hồ sơ doanh nghiệp')}
          </Button>
        </div>
      </form>
    </div>
  );
}
